import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CalendarDays, Database, RotateCcw } from "lucide-react";
import {
  HiMiniDocumentChartBar,
  HiMiniDocumentCheck,
  HiMiniDocumentMagnifyingGlass,
} from "react-icons/hi2";
import { RiDiscountPercentFill } from "react-icons/ri";
import { AiFillAmazonCircle, AiFillEuroCircle } from "react-icons/ai";
import { MdAccountBalanceWallet } from "react-icons/md";
import { PiHashStraightFill, PiPackageFill } from "react-icons/pi";
import { FaBalanceScale } from "react-icons/fa";
import { HiReceiptTax } from "react-icons/hi";
import DashboardCard from "../Components/DashboardCard";
import CategoryCard from "../Components/CategoryCard";
import "./styles/dashboard.css";

const EMPTY_COUNTS = {
  received_count: 0,
  missing_count: 0,
  manifest_count: 0,
  average_recovery_rate: 0,
  average_unit_recovery: 0,
  total_recovery_sum: 0,
  total_units_sum: 0,
  total_weight_sum: 0,
  total_unitCost_sum: 0,
  latest_manifest_publish_date: null,
};

const SOURCE_META = {
  Manifest: {
    label: "Manifest",
    cardLabel: "Manifest items",
    countKey: "manifest_count",
    icon: HiMiniDocumentChartBar,
    color: "#7c3aed",
  },
  ReceivedItems: {
    label: "Received",
    cardLabel: "Received items",
    countKey: "received_count",
    icon: HiMiniDocumentCheck,
    color: "#059669",
  },
  MissingItems: {
    label: "Missing",
    cardLabel: "Missing items",
    countKey: "missing_count",
    icon: HiMiniDocumentMagnifyingGlass,
    color: "#dc6b19",
  },
};

const formatApiDate = (date) => {
  const adjustedDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60000
  );
  return adjustedDate.toISOString().split("T")[0];
};

export default function HomePage() {
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsReadyKey, setMetricsReadyKey] = useState(null);
  const [selectedTable, setSelectedTable] = useState(
    localStorage.getItem("selectedTable") || "Manifest"
  );
  const [startDate, setStartDate] = useState(
    localStorage.getItem("startDate")
      ? new Date(localStorage.getItem("startDate"))
      : null
  );
  const [endDate, setEndDate] = useState(
    localStorage.getItem("endDate")
      ? new Date(localStorage.getItem("endDate"))
      : null
  );
  const startDatePickerRef = useRef(null);
  const endDatePickerRef = useRef(null);
  const baseUrl = process.env.REACT_APP_DJANGO_API_BASE_URL;
  const source = SOURCE_META[selectedTable] || SOURCE_META.Manifest;
  const dashboardRequestKey = `${selectedTable}|${startDate?.getTime() || ""}|${endDate?.getTime() || ""}`;

  useEffect(() => {
    const controller = new AbortController();
    setMetricsLoading(true);
    setMetricsReadyKey(null);

    axios
      .get(`${baseUrl}/api/dashboard_item_counts/`, {
        params: {
          table: selectedTable,
          start_date: startDate ? formatApiDate(startDate) : null,
          end_date: endDate ? formatApiDate(endDate) : null,
        },
        signal: controller.signal,
      })
      .then((response) => setCounts(response.data))
      .catch((error) => {
        if (error.code !== "ERR_CANCELED") {
          console.error("There was an error fetching the dashboard counts!", error);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setMetricsLoading(false);
          setMetricsReadyKey(dashboardRequestKey);
        }
      });

    return () => controller.abort();
  }, [baseUrl, selectedTable, startDate, endDate, dashboardRequestKey]);

  useEffect(() => {
    localStorage.setItem("selectedTable", selectedTable);
  }, [selectedTable]);

  useEffect(() => {
    if (startDate) localStorage.setItem("startDate", startDate.toISOString());
  }, [startDate]);

  useEffect(() => {
    if (endDate) localStorage.setItem("endDate", endDate.toISOString());
  }, [endDate]);

  const metricCards = useMemo(
    () => [
      {
        count: counts.average_unit_recovery?.toFixed(2) ?? "0",
        label: "Average recovery",
        name: "average-unit-recovery",
        icon: AiFillEuroCircle,
        color: "#2563eb",
        suffix: " €",
      },
      {
        count: counts.average_recovery_rate?.toFixed(2) ?? "0",
        label: "Average recovery rate",
        name: "average-recovery-rate",
        icon: RiDiscountPercentFill,
        color: "#7c3aed",
        suffix: "%",
      },
      {
        count: counts.total_unitCost_sum?.toFixed(2) ?? "0",
        label: "Amazon amount",
        name: "total-unitCost-sum",
        icon: AiFillAmazonCircle,
        color: "#d97706",
        suffix: " €",
      },
      {
        count: counts.total_recovery_sum?.toFixed(2) ?? "0",
        label: "Recovery amount",
        name: "total-recovery-sum",
        icon: MdAccountBalanceWallet,
        color: "#0891b2",
        suffix: " €",
      },
      {
        count: counts.total_units_sum ?? "0",
        label: "Total units",
        name: "total-units-sum",
        icon: PiHashStraightFill,
        color: "#2563eb",
      },
      {
        count: counts.total_weight_sum?.toFixed(2) ?? "0",
        label: "Total weight",
        name: "total-weight-sum",
        icon: FaBalanceScale,
        color: "#4f46e5",
        suffix: " kg",
      },
      {
        count: counts.unique_bol_count ?? "0",
        label: "Packets",
        name: "total-packets",
        icon: PiPackageFill,
        color: "#059669",
      },
      {
        count: counts.total_taxAmount_sum?.toFixed(2) ?? "0",
        label: "Tax amount",
        name: "total_taxAmount_sum",
        icon: HiReceiptTax,
        color: "#db2777",
        suffix: " €",
      },
    ],
    [counts]
  );

  const handleClearDates = () => {
    setStartDate(null);
    setEndDate(null);
    localStorage.removeItem("startDate");
    localStorage.removeItem("endDate");
  };

  const closeDatePickerAfterSelection = (pickerRef) => {
    window.requestAnimationFrame(() => pickerRef.current?.setOpen(false));
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  return (
    <div className="dashboard-page">
      <header className="dashboard-toolbar">
        <h1>Dashboard</h1>

        <div className="dashboard-controls" aria-label="Dashboard filters">
          <label className="dashboard-field" htmlFor="table-select">
            <span>Source</span>
            <select
              id="table-select"
              value={selectedTable}
              onChange={(event) => setSelectedTable(event.target.value)}
              className="source-dropdown"
            >
              <option value="Manifest">Manifest Items</option>
              <option value="ReceivedItems">Received Items</option>
              <option value="MissingItems">Missing Items</option>
            </select>
          </label>

          <label className="dashboard-field dashboard-date-field">
            <span>From</span>
            <div className="date-input-wrap">
              <CalendarDays size={17} aria-hidden="true" />
              <DatePicker
                ref={startDatePickerRef}
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                onSelect={() => closeDatePickerAfterSelection(startDatePickerRef)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                maxDate={endDate}
                placeholderText="Start date"
                dateFormat="dd MMM yyyy"
              />
            </div>
          </label>

          <label className="dashboard-field dashboard-date-field">
            <span>To</span>
            <div className="date-input-wrap">
              <CalendarDays size={17} aria-hidden="true" />
              <DatePicker
                ref={endDatePickerRef}
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                onSelect={() => closeDatePickerAfterSelection(endDatePickerRef)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                placeholderText="End date"
                dateFormat="dd MMM yyyy"
              />
            </div>
          </label>

          <button
            type="button"
            className="clear-button"
            onClick={handleClearDates}
            disabled={!startDate && !endDate}
            aria-label="Clear date range"
            title="Clear date range"
          >
            <RotateCcw size={17} aria-hidden="true" />
          </button>
        </div>

        <div className="database-status" aria-live="polite">
          <Database size={19} aria-hidden="true" />
          <div>
            <span>Database updated</span>
            <strong>
              {counts.latest_manifest_publish_date
                ? formatDate(counts.latest_manifest_publish_date)
                : "Fetching…"}
            </strong>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="metrics-grid">
          <DashboardCard
            count={counts[source.countKey]}
            label={source.cardLabel}
            name={`source-count-card ${selectedTable.toLowerCase()}-source-card`}
            icon={source.icon}
            iconColor={source.color}
            table={source.label}
            loading={metricsLoading}
            accentColor={source.color}
          />

          {metricCards.map((card) => (
            <DashboardCard
              key={card.name}
              count={card.count}
              label={card.label}
              name={card.name}
              icon={card.icon}
              iconColor={card.color}
              table={source.label}
              suffix={card.suffix}
              loading={metricsLoading}
              accentColor={card.color}
            />
          ))}
        </div>

        <CategoryCard
          selectedTable={selectedTable}
          displaySource={source.label}
          startDate={startDate}
          endDate={endDate}
          dashboardView
          enabled={metricsReadyKey === dashboardRequestKey}
        />
      </div>
    </div>
  );
}
